import React, { useMemo, useState } from "react";
import NavBar from "../components/layout/NavBar";
import "../components/common/common.css";
import { useTranslation } from "react-i18next";

// A single criteria node in the drill-down tree
function CriteriaNode({ node, index, onChange, onRemove, onAddBelow }) {
  const [local, setLocal] = useState(node);
  const { t } = useTranslation();

  const handleChange = (key, value) => {
    const updated = { ...local, [key]: value };
    setLocal(updated);
    onChange(index, updated);
  };

  return (
    <div style={styles.node}>
      <div style={styles.row}>
        <select
          style={styles.select}
          value={local.field}
          onChange={(e) => handleChange("field", e.target.value)}
        >
          <option value="">{t("searchBuilder.selectField")}</option>
          <option value="CustomerNumber">
            {t("searchBuilder.field.customerNumber")}
          </option>
          <option value="Customer">{t("searchBuilder.field.customer")}</option>
          <option value="CreditCard">
            {t("searchBuilder.field.creditCard")}
          </option>
          <option value="DocumentDate">
            {t("searchBuilder.field.documentDate")}
          </option>
          <option value="InvoiceNumber">
            {t("searchBuilder.field.invoiceNumber")}
          </option>
          <option value="Amount">{t("searchBuilder.field.amount")}</option>
        </select>
        <select
          style={styles.select}
          value={local.operator}
          onChange={(e) => handleChange("operator", e.target.value)}
        >
          <option value="=">{t("searchBuilder.op.eq")}</option>
          <option value="contains">{t("searchBuilder.op.contains")}</option>
          <option value=">">{t("searchBuilder.op.gt")}</option>
          <option value="<">{t("searchBuilder.op.lt")}</option>
          <option value=">=">{t("searchBuilder.op.gte")}</option>
          <option value="<=">{t("searchBuilder.op.lte")}</option>
          <option value="between">{t("searchBuilder.op.between")}</option>
        </select>
        <input
          style={styles.input}
          placeholder={t("searchBuilder.placeholderValue")}
          value={local.value}
          onChange={(e) => handleChange("value", e.target.value)}
        />
      </div>
      <div style={styles.row}>
        <button
          className="icon-btn"
          onClick={() => onAddBelow(index)}
          title={t("searchBuilder.addBelow")}
        >
          ＋
        </button>
        <button
          className="icon-btn"
          onClick={() => onRemove(index)}
          title={t("searchBuilder.removeLevel")}
        >
          ✕
        </button>
      </div>
    </div>
  );
}

export default function SearchBuilder() {
  const { t } = useTranslation();
  const [name, setName] = useState("Call Center: Customer → Card → Date");
  const [levels, setLevels] = useState([
    { field: "CustomerNumber", operator: "=", value: "" },
  ]);

  const addBelow = (i) => {
    const copy = levels.slice();
    copy.splice(i + 1, 0, { field: "", operator: "=", value: "" });
    setLevels(copy);
  };

  const removeAt = (i) => {
    const copy = levels.slice();
    copy.splice(i, 1);
    setLevels(copy.length ? copy : [{ field: "", operator: "=", value: "" }]);
  };

  const updateAt = (i, updated) => {
    const copy = levels.slice();
    copy[i] = updated;
    setLevels(copy);
  };

  const json = useMemo(
    () => JSON.stringify({ name, levels }, null, 2),
    [name, levels]
  );

  const saveLayout = () => {
    const all = JSON.parse(localStorage.getItem("search_layouts") || "[]");
    const existsIdx = all.findIndex((x) => x.name === name);
    if (existsIdx >= 0) all[existsIdx] = { name, levels };
    else all.push({ name, levels });
    localStorage.setItem("search_layouts", JSON.stringify(all));
    alert(t("searchBuilder.savedLayout"));
  };

  return (
    <div className="app-root">
      <div className="nav-strip">
        <NavBar />
      </div>
      <header className="top-bar">
        <div className="ai-chip">{t("searchBuilder.title")}</div>
        <div className="search-wrapper" style={{ maxWidth: 560 }}>
          <input
            className="search-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <button className="go-btn" onClick={saveLayout}>
            {t("common.save")}
          </button>
        </div>
        <div style={{ flex: 1 }} />
      </header>

      <div className="main-layout" style={{ gridTemplateColumns: "1fr 1fr" }}>
        <section className="center-panel">
          <h3 style={{ marginTop: 0 }}>{t("searchBuilder.drillLevels")}</h3>
          {levels.map((lvl, i) => (
            <CriteriaNode
              key={i}
              node={lvl}
              index={i}
              onChange={updateAt}
              onRemove={removeAt}
              onAddBelow={addBelow}
            />
          ))}
          <button
            className="icon-btn"
            onClick={() => addBelow(levels.length - 1)}
            title={t("searchBuilder.addNewLevel")}
          >
            ＋
          </button>
        </section>

        <aside className="detail-panel">
          <h3 style={{ marginTop: 0 }}>{t("searchBuilder.previewJson")}</h3>
          <pre style={styles.pre}>{json}</pre>
          <h4>{t("searchBuilder.savedLayouts")}</h4>
          <SavedLayouts
            onLoad={(layout) => {
              setName(layout.name);
              setLevels(layout.levels);
            }}
          />
        </aside>
      </div>
    </div>
  );
}

function SavedLayouts({ onLoad }) {
  const [items, setItems] = useState(() =>
    JSON.parse(localStorage.getItem("search_layouts") || "[]")
  );
  const { t } = useTranslation();

  const remove = (name) => {
    const next = items.filter((x) => x.name !== name);
    setItems(next);
    localStorage.setItem("search_layouts", JSON.stringify(next));
  };

  if (!items.length)
    return <div className="skeleton-line" style={{ width: "60%" }} />;

  return (
    <ul style={{ listStyle: "none", padding: 0 }}>
      {items.map((x, i) => (
        <li key={i} style={styles.savedItem}>
          <span>{x.name}</span>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="icon-btn"
              title={t("searchBuilder.load")}
              onClick={() => onLoad(x)}
            >
              ⤓
            </button>
            <button
              className="icon-btn"
              title={t("searchBuilder.delete")}
              onClick={() => remove(x.name)}
            >
              ✕
            </button>
          </div>
        </li>
      ))}
    </ul>
  );
}

const styles = {
  node: {
    border: "2px solid #222",
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
    background: "#fff",
  },
  row: { display: "flex", gap: 8, alignItems: "center", marginBottom: 6 },
  select: {
    padding: "8px 10px",
    border: "1.5px solid #222",
    borderRadius: 8,
    background: "#fff",
  },
  input: {
    padding: "8px 10px",
    border: "1.5px solid #222",
    borderRadius: 8,
    flex: 1,
  },
  pre: {
    background: "#111",
    color: "#f1f1f1",
    padding: 12,
    borderRadius: 8,
    overflow: "auto",
  },
  savedItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 10px",
    border: "1px solid #ccc",
    borderRadius: 8,
    marginBottom: 8,
    background: "#fff",
  },
};
